import { useIntl } from 'react-intl';
import { generatePath, useMatch, useParams } from 'react-router';

import { BreadcrumbElementType, PATHS } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { getName } from '@/shared/lib';

import { patientModel } from '@/entities/patient';
import { userModel } from '@/entities/user';

import { useGetReportConfigs } from './useGetReportConfigs';

export const useMobileBreadcrumbs = (
  currentPageLabel = '',
): Record<string, BreadcrumbElementType> => {
  const params = useParams();
  const patientMatch = useMatch(PATHS.patient);
  const dentalPhotosMatch = useMatch(PATHS.dentalPhotos);
  const recordingsMatch = useMatch(PATHS.patientRecordings);
  const { formatMessage } = useIntl();

  const patientID = params.patientID ?? '';
  const reportID = params.reportID ?? '';
  const isPatientProfile = patientMatch !== null;
  const isDentalPhotosPage = dentalPhotosMatch !== null;
  const isRecordingsPage = recordingsMatch !== null;

  const currentPatient = useAppSelector(
    patientModel.selectors.selectPatientByID(patientID),
  );
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const { reportLabel: reportTypeLabel } = useGetReportConfigs({
    patientID,
    reportID,
    isWide: false,
    isMobile: true,
  });

  if (params.documentID) {
    // Chat state (selected thread etc.) lives in localStorage, not the URL.
    return {
      document: {
        label: currentPageLabel,
        path: generatePath(PATHS.patient, { patientID }),
      },
    };
  }

  if (isPatientProfile) {
    const patientLabel = getName(
      currentPatient?.PersonalData?.FirstName,
      currentPatient?.PersonalData?.LastName,
      {
        userLocale,
        shortFirstName: false,
        shortLastName: false,
      },
    );

    return {
      patient: {
        label: patientLabel,
        path: PATHS.patients,
      },
    };
  }

  if (isDentalPhotosPage) {
    const patientProfilePath = generatePath(PATHS.patient, {
      patientID,
    });

    return {
      dentalPhotos: {
        label: formatMessage({
          id: 'dentalPhotosPage.title',
          defaultMessage: 'Dental photos',
        }),
        path: patientProfilePath,
      },
    };
  }

  if (isRecordingsPage) {
    return {
      recordings: {
        label: formatMessage({
          id: 'patientRecordings.title',
          defaultMessage: 'Recordings',
        }),
        path: generatePath(PATHS.patient, { patientID }),
      },
    };
  }

  if (reportID) {
    const patientProfilePath = generatePath(PATHS.patient, {
      patientID,
    });

    return {
      report: {
        label: reportTypeLabel,
        path: patientProfilePath,
      },
    };
  }

  return {};
};
